'use client';

import { FC, useEffect, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Bell, Check } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { addExpansionRequest } from '@/hotdesk-ui/store/slices/expansion-requests.slice';
import { AuthPopupService } from '@/hotdesk-ui/services/authPopup.service';
import { tracking } from '@/hotdesk-ui/services/tracking.service';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import styles from './ExpansionRequestBanner.module.scss';
import Image from 'next/image';
const NORTH_AMERICA_COUNTRY_CODES = new Set(['US', 'CA']);
const isTrustedLocation = (location?: GeoCodeResult | null) => Boolean(location) && location?.source !== 'remote-default';

/** Pick one location record so city, country, and state always agree. */
const pickLocationSource = (currentSearch: GeoCodeResult | null, geoLocation: GeoCodeResult | null): GeoCodeResult | null => {
  if (isTrustedLocation(currentSearch)) return currentSearch;
  if (isTrustedLocation(geoLocation)) return geoLocation;
  return currentSearch ?? geoLocation;
};
export interface ExpansionRequestBannerProps {
  /** ISO-2 country code (e.g. "BE"). Banner won't render if absent. */
  countryCode?: string;
  /** Optional city code to include in the tracking payload. */
  cityCode?: string;
  /** Optional state/region name (used for US & Canada copy). */
  stateCode?: string;
}
const ExpansionRequestBanner: FC<ExpansionRequestBannerProps> = ({
  countryCode: countryCodeProp,
  stateCode: stateCodeProp
}) => {
  const t = useTranslations('landing-v4.expansionRequest');
  const locale = useLocale();
  const dispatch = useAppDispatch();
  const user = useAppSelector((s: RootState) => s.auth.user);
  const requestedCountries = useAppSelector((s: RootState) => s.expansionRequests.countries);
  const geoLocation = useAppSelector((s: RootState) => s.geoLocation.geoLocationZipData);
  const currentSearch = useAppSelector((s: RootState) => s.search.currentSearch);
  const filters = useAppSelector((s: RootState) => s.listing.filters);
  const [pending, setPending] = useState(false);

  // Read city, country, and state from the same location record (search first,
  // then geo) so we never mix e.g. a searched city with the visitor's IP country.
  const location = pickLocationSource(currentSearch, geoLocation);
  const cityCode = location?.city;
  const countryCode = location?.country_sort ?? countryCodeProp;
  const stateCode = location?.state ?? stateCodeProp;
  const code = countryCode?.toUpperCase();
  const alreadyRequested = !!code && requestedCountries.includes(code);

  // Resolve a localized country name for the CTA copy.
  const countryName = (() => {
    if (!code || code.length !== 2) return code || '';
    try {
      const dn = new Intl.DisplayNames([locale], {
        type: 'region'
      });
      return dn.of(code) || code;
    } catch {
      return code;
    }
  })();
  const hasCity = Boolean(cityCode);
  const usesStateFormat = !!code && NORTH_AMERICA_COUNTRY_CODES.has(code) && Boolean(stateCode);
  const locationParams = {
    country: countryName,
    ...(hasCity ? {
      city: cityCode
    } : {}),
    ...(usesStateFormat ? {
      state: stateCode
    } : {})
  };
  const titleKey = alreadyRequested ? usesStateFormat ? hasCity ? 'confirmedTitleWithState' : 'confirmedTitleWithStateCountryOnly' : hasCity ? 'confirmedTitle' : 'confirmedTitleCountryOnly' : usesStateFormat ? hasCity ? 'titleWithState' : 'titleWithStateCountryOnly' : hasCity ? 'title' : 'titleCountryOnly';
  const submit = () => {
    if (!code) return;
    const screenUrl = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';
    tracking.onWorkspaceRequestSubmitted(geoLocation, {
      ...(filters || {}),
      country: code
    }, screenUrl, user?.email);
    dispatch(addExpansionRequest(code));
    setPending(false);
  };

  // If a sign-in was triggered by clicking the CTA, auto-submit once the
  // user becomes authenticated.
  useEffect(() => {
    if (pending && user) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pending]);
  if (!code) return null;
  const handleClick = () => {
    if (alreadyRequested) return;
    if (!user) {
      setPending(true);
      new AuthPopupService().getPopup();
      return;
    }
    submit();
  };
  return <div className={styles.banner} role="status" aria-live="polite" data-sentry-component="ExpansionRequestBanner" data-sentry-source-file="ExpansionRequestBanner.tsx">
            <div className={styles.iconWrap}>
                <Image width={20} height={21} alt="no-workspace" src="/assets/icons/no-workspace.svg" className={styles.icon} data-sentry-element="Image" data-sentry-source-file="ExpansionRequestBanner.tsx" />
            </div>
            <div className={styles.copy}>
                <p className={styles.title}>
                    {t(titleKey, locationParams)}
                </p>
                <p className={styles.subtitle}>
                    {alreadyRequested ? t('confirmedSubtitle') : t('subtitle')}
                </p>
            </div>
            <button type="button" className={`${styles.cta} ${alreadyRequested ? styles.ctaConfirmed : ''}`} onClick={handleClick} disabled={alreadyRequested}>
                {alreadyRequested ? <>
                        <Check size={16} />
                        {t('confirmedCta')}
                    </> : <>
                        <Bell size={16} />
                        {user ? t('cta') : t('signedOutCta')}
                    </>}
            </button>
        </div>;
};
export default ExpansionRequestBanner;